<script setup lang="ts">
import { computed, ref, watch, inject, onBeforeMount, onUpdated } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  EcxButton,
  EcxCard,
  EcxField,
  EcxInputText,
  EcxInputPassword,
  EcxLoginShell,
} from "@edgeconnex/design-system";
import userAPI from "@/apiServices/userAPI";
import { handleEntra } from "@/handleEntra";
import { refreshAuthenticatedSessionState } from "@/services/sessionBootstrap";
import type { ILoggedUserService } from "@/services/LoggedUserService";
import { useBuildFooterData } from "@/composables/useBuildFooterData";
import { useEnvironmentLabel } from "@/composables/useEnvironmentLabel";

const loggedUserService = inject<ILoggedUserService>("loggedUserService")!;
const route = useRoute();
const router = useRouter();

const { isDevMode, footerData } = useBuildFooterData();
// Which system am I signing in to? Matters most here, before the user commits to a session (PR-869).
const { environmentLabel, showEnvironmentTheme } = useEnvironmentLabel(isDevMode, "DEV");

const formattedBuildTime = computed(() => {
  const raw = footerData.value.buildTime;
  if (!raw) return "";
  const isoish = raw.replace(" ", "T").replace(/\s+([+-]\d{2}:?\d{2})$/, "$1");
  const date = new Date(isoish);
  if (Number.isNaN(date.getTime())) return raw;
  const datePart = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} ${timePart}`;
});

const loginFooterItems = computed(() =>
  [
    footerData.value.copyright,
    `ver. ${footerData.value.buildNo || "-"}`,
    formattedBuildTime.value ? `Updated ${formattedBuildTime.value}` : "",
    footerData.value.serverName,
    footerData.value.serverIP,
    footerData.value.dbName ? `db: ${footerData.value.dbName}` : "",
  ].filter((item): item is string => Boolean(item)),
);

const { login } = handleEntra();

async function navigateAfterLogin() {
  // Same sequence as the full-page bootstrap — user, tenant init, then scoped services —
  // so the SPA login path gets X-Tenant and CASL subjects set up identically.
  await refreshAuthenticatedSessionState();
  const redirect = (route.query.redirect as string) || "/";
  await router.push(redirect);
}

const username = ref("");
const password = ref("");
const error = ref("");
const foundUser = ref(false);
const loading = ref(false);
let authMethod = "";

function isConnectionTimeout(error: unknown): boolean {
  const err = error as { code?: string; message?: string; response?: unknown };
  return (
    err?.code === "ECONNABORTED" ||
    err?.code === "ERR_NETWORK" ||
    err?.message?.toLowerCase().includes("timeout") ||
    (!err?.response && !!err?.code)
  );
}

watch(username, () => {
  error.value = "";
});
watch(password, () => {
  error.value = "";
});

const fetchAuthMethod = async (emailAddress: string) => {
  try {
    const response = await userAPI.getAuthMethod(emailAddress);
    if (response.status === 200) {
      authMethod = (response.data.authenticationMethod ?? "").toLowerCase();
      return true;
    }
  } catch (err) {
    if (isConnectionTimeout(err)) {
      error.value = "Could not connect to CMMS. Please check your network connection and try again.";
    }
  }
  return false;
};

async function determineNextStep() {
  const normalizedUsername = username.value.trim();

  if (!normalizedUsername) {
    error.value = "Email is required.";
    return;
  }

  username.value = normalizedUsername;
  loading.value = true;
  const methodFound = await fetchAuthMethod(normalizedUsername);

  if (methodFound) {
    if (authMethod === "local" || authMethod === "entra") {
      foundUser.value = true;
      error.value = "";
    } else {
      error.value = "This account is not configured for sign-in.";
    }
  } else if (!error.value) {
    error.value = "Account not found.";
  }

  loading.value = false;
}

function clearUser() {
  authMethod = "";
  error.value = "";
  password.value = "";
  foundUser.value = false;
  sessionStorage.removeItem("authError");
}

function loginUserSSO() {
  username.value = username.value.trim();
  sessionStorage.setItem("username", username.value);
  void login();
}

async function loginUserLocally() {
  username.value = username.value.trim();
  if (!password.value) {
    error.value = "Password is required.";
    return;
  }

  try {
    loading.value = true;
    const response = await userAPI.loginWithPassword(username.value, password.value);

    sessionStorage.removeItem("authError");
    loggedUserService.setSessionToken(response.data.token);
    loggedUserService.setSessionExpiration(response.data.expirationTS);
    await navigateAfterLogin();
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      error.value = "Invalid username or password.";
    } else if (isConnectionTimeout(e)) {
      error.value = "Could not connect to CMMS. Please check your network connection and try again.";
    } else {
      error.value = "Could not sign in. Please try again.";
    }
    loading.value = false;
  }
}

function checkAuthError() {
  const msg = sessionStorage.getItem("authError");
  if (msg) {
    error.value = msg;
    sessionStorage.removeItem("authError");
  }
}

onBeforeMount(() => {
  checkAuthError();
});

onUpdated(() => {
  if (foundUser.value && authMethod === "local") {
    document.getElementById("txtPassword")?.focus();
  }
});
</script>

<template>
  <EcxLoginShell>
    <EcxCard class="min-w-96">
      <!-- Title -->
      <template #title>
        <div class="flex items-center justify-center gap-2">
          <span class="text-3xl font-semibold text-content">CMMS</span>
          <span
            v-if="showEnvironmentTheme"
            class="inline-flex h-6 min-w-6 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-red-900 px-2 text-sm font-semibold leading-none text-white"
          >
            {{ environmentLabel }}
          </span>
        </div>
      </template>

      <template #content>
        <div class="flex flex-col gap-4">
          <!-- Step 1: Email -->
          <div class="flex flex-col gap-3" v-if="!foundUser">
            <EcxField :errorMessage="error">
              <EcxInputText
                id="txtUsername"
                v-model="username"
                placeholder="Enter your email"
                :invalid="!!error"
                :disabled="loading"
                @keyup.enter="determineNextStep"
              />
            </EcxField>
            <EcxButton :loading="loading" fluid @click="determineNextStep"> Continue </EcxButton>
          </div>

          <!-- Step 2a: Local password -->
          <div class="flex flex-col gap-4" v-else-if="authMethod === 'local'">
            <div class="flex flex-col items-start gap-1">
              <p class="m-0 text-sm text-content-muted break-all">{{ username }}</p>
              <EcxButton :loading="loading" variant="link" size="sm" @click="clearUser">
                Change User
              </EcxButton>
            </div>
            <EcxField label="Password" labelFor="txtPassword" :errorMessage="error">
              <EcxInputPassword
                id="txtPassword"
                v-model="password"
                :invalid="!!error"
                @keyup.enter="loginUserLocally"
              />
            </EcxField>
            <EcxButton :loading="loading" fluid @click="loginUserLocally"> Sign in </EcxButton>
          </div>

          <!-- Step 2b: Entra SSO -->
          <div
            class="flex flex-col gap-4 items-center justify-center"
            v-else-if="authMethod === 'entra'"
          >
            <div class="flex flex-col gap-1 w-full items-center justify-center">
              <div class="py-2 px-4 w-fit border border-dashed border-border-muted rounded-full flex items-center">
                {{ username }}
              </div>
              <EcxButton :loading="loading" fluid @click="clearUser" variant="link" size="sm"
                >Change User</EcxButton
              >
            </div>
            <EcxButton :loading="loading" fluid @click="loginUserSSO">
              Sign in with SSO
            </EcxButton>
          </div>
        </div>
      </template>
    </EcxCard>

    <template #footer>
      <div
        data-testid="login-footer"
        class="mx-auto flex max-w-5xl flex-wrap justify-center gap-x-2 gap-y-1 px-6 text-center text-xs leading-5 text-white"
      >
        <span
          v-for="(item, index) in loginFooterItems"
          :key="item"
          :class="[
            'whitespace-nowrap',
            index > 0 ? 'border-l border-white/30 pl-2' : '',
          ]"
        >
          {{ item }}
        </span>
      </div>
    </template>
  </EcxLoginShell>
</template>
